// import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// import { RootState } from '../index';

// interface LeadershipWalkRedirectState {
//   questions: any[];
//   loading: boolean;
//   error: string | null;
// }

// const initialState: LeadershipWalkRedirectState = {
//   questions: [],
//   loading: false,
//   error: null,
// };

// const leadershipWalkRedirectSlice = createSlice({
//   name: 'leadershipWalkRedirect',
//   initialState,
//   reducers: {
//     setQuestions: (state, action: PayloadAction<any[]>) => {
//       state.questions = action.payload;
//     },
//     setLoading: (state, action: PayloadAction<boolean>) => {
//       state.loading = action.payload;
//     },
//     setError: (state, action: PayloadAction<string | null>) => {
//       state.error = action.payload;
//     },
//   },
// });

// export const { setQuestions, setLoading, setError } = leadershipWalkRedirectSlice.actions;

// // Selectors
// export const selectQuestions = (state: RootState) => state.leadershipWalkRedirect.questions;
// export const selectLoading = (state: RootState) => state.leadershipWalkRedirect.loading;
// export const selectError = (state: RootState) => state.leadershipWalkRedirect.error;

// export default leadershipWalkRedirectSlice.reducer;

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../index";

interface Option {
  //SelectedOption
  id: string;
  label: string;
  details?: string; // Add details field for the flip functionality
}

export interface QuestionsBySection {
  [key: string]: any[];
}

interface ImageData {
  //LeadershipImageData
  imageId: string;
  imageUrl?: string; // URL from the image upload response
  fileName?: string; // fileName from the image upload response
  imagePreview?: string;
  observationId: string;
  description: string;
}

// Interface for miscellaneous observation images
interface MiscObservationImage {
  imageUrl: string;
  fileName: string;
  description: string;
  index: number;
}

interface LeadershipWalkRecord {
  createdBy: string;
  created: string;
  id: string;
  name?: string;
  // Add other properties that selectedRecord might have
}

interface LeadershipWalkRedirectState {
  questions: QuestionsBySection;
  selectedQuestions: any; // For storing walkResponse
  loading: boolean;
  error: string | null;
  images: ImageData[];
  validationErrors: Record<string, boolean>;
  walkState: "not_started" | "in_progress" | "completed";
  managerSignatureFileName: string | null;
  managerSignatureImageUrl: string | null;
  isManagerSignatureUpdated: boolean;
  selectedRecord: LeadershipWalkRecord | null;
  selectedOptions: Option[];
  // New fields for miscellaneous observations
  miscObservationImages: MiscObservationImage[];
  miscellaneousObservations: string;
}

const initialState: LeadershipWalkRedirectState = {
  questions: {},
  selectedQuestions: null,
  loading: false,
  error: null,
  images: [],
  validationErrors: {},
  walkState: "not_started",
  managerSignatureFileName: null,
  managerSignatureImageUrl: null,
  isManagerSignatureUpdated: false,
  selectedRecord: null,
  selectedOptions: [],
  // Initialize the new fields
  miscObservationImages: [],
  miscellaneousObservations: "",
};

const leadershipWalkRedirectSlice = createSlice({
  name: "leadershipWalkRedirect",
  initialState,
  reducers: {
    setQuestions: (state, action: PayloadAction<QuestionsBySection>) => {
      state.questions = action.payload;
    },
    setSelectedQuestions: (state, action: PayloadAction<any>) => {
      state.selectedQuestions = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    toggleOption: (state, action: PayloadAction<Option>) => {
      const existingIndex = state.selectedOptions.findIndex(
        (option) => option.id === action.payload.id
      );
      if (existingIndex > -1) {
        state.selectedOptions.splice(existingIndex, 1);
      } else {
        state.selectedOptions.push(action.payload);
      }
    },
    addImage: (state, action: PayloadAction<ImageData>) => {
      const existingIndex = state.images.findIndex(
        (img) =>
          img.imageId === action.payload.imageId &&
          img.observationId === action.payload.observationId
      );
      if (existingIndex >= 0) {
        state.images[existingIndex] = action.payload;
      } else {
        state.images.push(action.payload);
      }
    },
    setValidationErrors(state, action: PayloadAction<Record<string, boolean>>) {
      state.validationErrors = action.payload;
    },
    clearValidationErrors(state) {
      state.validationErrors = {};
    },
    setWalkState: (
      state,
      action: PayloadAction<"not_started" | "in_progress" | "completed">
    ) => {
      state.walkState = action.payload;
    },
    setManagerSignature(
      state,
      action: PayloadAction<{
        fileName: string | null;
        imageUrl?: string | null;
        isNewUpload?: boolean;
      }>
    ) {
      state.managerSignatureFileName = action.payload.fileName;
      state.managerSignatureImageUrl = action.payload.imageUrl || null;
      if (action.payload.isNewUpload) {
        state.isManagerSignatureUpdated = true;
      }
    },
    resetManagerSignatureFlag(state) {
      state.isManagerSignatureUpdated = false;
    },
    setSelectedRecord: (
      state,
      action: PayloadAction<LeadershipWalkRecord | null>
    ) => {
      state.selectedRecord = action.payload;
    },
    // Add new action for setting miscellaneous observation image
    setMiscObservationImage: (
      state,
      action: PayloadAction<{
        index: number;
        image: MiscObservationImage;
      }>
    ) => {
      // Initialize the array if it doesn't exist
      if (!state.miscObservationImages) {
        state.miscObservationImages = [];
      }

      // Update or add the image at the specified index
      const { index, image } = action.payload;
      if (index < state.miscObservationImages.length) {
        state.miscObservationImages[index] = image;
      } else {
        // Fill in any gaps with empty images
        while (state.miscObservationImages.length < index) {
          state.miscObservationImages.push({
            imageUrl: "",
            fileName: "",
            description: "",
            index: state.miscObservationImages.length,
          });
        }
        state.miscObservationImages.push(image);
      }
    },
    // Add new action for setting miscellaneous observations text
    setMiscellaneousObservations: (state, action: PayloadAction<string>) => {
      state.miscellaneousObservations = action.payload;
    },
    // Reset selected options only
    resetSelectedOptions: (state) => {
      state.selectedOptions = [];
    },
    // Reset all state to initial values
    resetLeadershipWalkRedirect: () => {
      return initialState;
    },
    // Memory management actions
    clearImages: (state) => {
      // Clear image data but keep metadata
      state.images = state.images.map(img => ({
        ...img,
        imagePreview: undefined, // Remove base64 data
      }));
      console.log('Leadership Walk: Image previews cleared for memory optimization');
    },
    clearMiscImages: (state) => {
      state.miscObservationImages = [];
      console.log('Leadership Walk: Miscellaneous observation images cleared');
    },
    limitImages: (state, action: PayloadAction<{ maxImages: number }>) => {
      const { maxImages } = action.payload;
      
      if (state.images.length > maxImages) {
        state.images = state.images.slice(-maxImages); // Keep most recent
        console.log(`Leadership Walk: Limited images to ${maxImages} most recent`);
      }
      
      if (state.miscObservationImages.length > maxImages) {
        state.miscObservationImages = state.miscObservationImages.slice(-maxImages);
        console.log(`Leadership Walk: Limited misc images to ${maxImages} most recent`);
      }
    },
    clearQuestionData: (state) => {
      // Clear question responses but keep structure
      state.selectedQuestions = {};
      state.selectedOptions = [];
      console.log('Leadership Walk: Question data cleared for memory optimization');
    },
  },
});

export const {
  setQuestions,
  setSelectedQuestions,
  setLoading,
  setError,
  toggleOption,
  addImage,
  clearValidationErrors,
  setValidationErrors,
  setWalkState,
  setManagerSignature,
  resetManagerSignatureFlag,
  setSelectedRecord,
  setMiscObservationImage,
  setMiscellaneousObservations,
  resetSelectedOptions,
  resetLeadershipWalkRedirect,
  // Memory management actions
  clearImages,
  clearMiscImages,
  limitImages,
  clearQuestionData,
} = leadershipWalkRedirectSlice.actions;

// Selectors
export const selectQuestions = (state: RootState) =>
  state.leadershipWalkRedirect.questions;
export const selectLoading = (state: RootState) =>
  state.leadershipWalkRedirect.loading;
export const selectError = (state: RootState) =>
  state.leadershipWalkRedirect.error;
export const selectSelectedOptions = (state: RootState) =>
  state.leadershipWalkRedirect.selectedOptions;
export const selectImages = (state: RootState) =>
  state.leadershipWalkRedirect.images;
export const selectWalkState = (state: RootState) =>
  state.leadershipWalkRedirect.walkState;
export const selectSelectedRecord = (state: RootState) =>
  state.leadershipWalkRedirect.selectedRecord;
export const selectSelectedQuestions = (state: RootState) =>
  state.leadershipWalkRedirect.selectedQuestions;
export const selectMiscObservationImages = (state: RootState) =>
  state.leadershipWalkRedirect.miscObservationImages;
export const selectMiscellaneousObservations = (state: RootState) =>
  state.leadershipWalkRedirect.miscellaneousObservations;

export default leadershipWalkRedirectSlice.reducer;
